import React, { ReactNode } from "react";
import { useDispatch } from "react-redux";
import * as sharedActions from "@shared/store/actions";

export interface UseCopyTextProps {
  successMessage?: string;
  failureMessage?: string;
  normalizer?: (content: ReactNode) => string;
}

export type HandleCopyFunc = (text: ReactNode) => Promise<void>;

const formatCopiedText = (content: string): string => {
  return content.replace(/\s+/g, " ").replace(/\s+,/g, ",").trim();
};

const normalizeCopiedText = (content: ReactNode): string => {
  if (typeof content === "string") return content;
  if (typeof content === "number") return String(content);
  if (Array.isArray(content)) return content.map(normalizeCopiedText).join(" ");
  if (React.isValidElement(content)) {
    return normalizeCopiedText(content.props.children);
  }
  return "";
};

export const useCopyText = ({
  successMessage = "Success! Copied to clipboard",
  failureMessage = "Error! Failed to copy text",
  normalizer = (content) => formatCopiedText(normalizeCopiedText(content)),
}: UseCopyTextProps = {}): HandleCopyFunc => {
  const dispatch = useDispatch();

  const handleCopy: HandleCopyFunc = async (text) => {
    const normalizedText = normalizer ? normalizer(text) : (text as string);

    try {
      await navigator.clipboard.writeText(normalizedText);

      if (successMessage) {
        dispatch(
          sharedActions.showNotification({
            message: successMessage,
            appearance: "success",
          }),
        );
      }
    } catch (err) {
      if (failureMessage) {
        dispatch(
          sharedActions.showNotification({
            message: failureMessage,
            appearance: "error",
          }),
        );
      }
    }
  };

  return handleCopy;
};
